import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2 } from 'lucide-react'
import type { LabelReconciliation } from '@/api/productLabelsApi'
import { reconciliationLine } from '@/lib/productLabels'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

/**
 * Complete Form's label step (z8nrz7dt48 AC4/AC5), shown whenever the form
 * printed labels. Balanced: the counts, then on to sign. Not balanced: review
 * the labels, or complete anyway with the discrepancy explained — the reason is
 * sent with the completion, whose own signature signs it.
 */
export function LabelReconciliationDialog({
  summary,
  open,
  onReview,
  onContinue,
  onCompleteWithReason,
  onClose,
}: {
  summary: LabelReconciliation | undefined
  open: boolean
  onReview: () => void
  /** Balanced: go on to the completion signature. */
  onContinue: () => void
  onCompleteWithReason: (reason: string) => void
  onClose: () => void
}) {
  const balanced = !!summary?.balanced

  const [explaining, setExplaining] = useState(false)
  const [reason, setReason] = useState('')

  useEffect(() => {
    if (open) {
      setExplaining(false)
      setReason('')
    }
  }, [open])

  return (
    <Dialog open={open && !!summary} onOpenChange={o => !o && onClose()}>
      <DialogContent className='sm:max-w-lg'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            {balanced ? (
              <>
                <CheckCircle2 className='h-5 w-5 text-emerald-600' />
                Labels reconcile
              </>
            ) : (
              <>
                <AlertTriangle className='h-5 w-5 text-amber-600' />
                Labels do not reconcile
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {balanced
              ? 'Every label printed for this form is accounted for. This result is recorded with the completion and its signature.'
              : 'Every label printed for this form must be applied (verified), discarded or returned unused before it completes.'}
          </DialogDescription>
        </DialogHeader>
        {summary && (
          <div className='space-y-3 text-sm'>
            <p
              className={
                balanced
                  ? 'rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-800'
                  : 'rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900'
              }
            >
              {reconciliationLine(summary)}
            </p>
            {summary.pendingLabels.length > 0 && (
              <div>
                <p className='mb-1 text-xs font-medium text-muted-foreground'>Not accounted for</p>
                <ul className='space-y-0.5'>
                  {summary.pendingLabels.map(l => (
                    <li key={l.id} className='font-mono text-xs'>
                      #{l.number} · {l.din}
                      {l.productCode ? ` · ${l.productCode}` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {explaining && (
              <div className='space-y-1.5'>
                <Label htmlFor='label-discrepancy-reason'>
                  Reason for the discrepancy<span className='text-destructive'> *</span>
                </Label>
                <Textarea
                  id='label-discrepancy-reason'
                  autoFocus
                  rows={3}
                  maxLength={1000}
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                />
                <p className='text-xs text-muted-foreground'>
                  Recorded on the form&apos;s audit trail with the completion signature.
                </p>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {balanced ? (
            <>
              <Button variant='outline' onClick={onReview}>
                Review labels
              </Button>
              <Button onClick={onContinue}>Continue to sign</Button>
            </>
          ) : explaining ? (
            <>
              <Button variant='outline' onClick={() => setExplaining(false)}>
                Back
              </Button>
              <Button disabled={!reason.trim()} onClick={() => onCompleteWithReason(reason.trim())}>
                Continue to sign
              </Button>
            </>
          ) : (
            <>
              <Button variant='outline' onClick={() => setExplaining(true)}>
                Complete with discrepancy
              </Button>
              <Button onClick={onReview}>Review labels</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
